import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ProductUserTypeEnum, ProductUserTypeGroupingEnum } from '@rma/billing-api';
import { VisibleProfileType } from '@rma/dashboard/generic/domain/to-be-sorted';
import { isFinanceVertical, isIndividual, isTeam } from '@rma/generic/domain/helpers';
import { INDIVIDUAL, OFFICE, TEAM } from '@rma/generic/domain/types';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { cache } from 'decorator-cache-getter';
import { distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';
import { BillingUserContext } from '../entities/billing-user-context.models';
import { DashboardProfileContextService } from './dashboard-context/dashboard-profile-context.service';

@Injectable({
  providedIn: 'root',
})
export class BillingUserContextService {
  private readonly route = inject(ActivatedRoute);
  private readonly dashboardProfileContextService = inject(DashboardProfileContextService);

  @cache
  public get userContext$(): Observable<BillingUserContext> {
    const queryContext$ = this.getQueryContext();
    return queryContext$.pipe(switchMap((queryContext) => (queryContext ? of(queryContext) : this.getUserContextInternal())));
  }

  private getUserContextInternal(): Observable<BillingUserContext> {
    return this.dashboardProfileContextService.profileIdentifierWithVertical$.pipe(
      map((profile) => {
        const productUserType = isFinanceVertical(profile.verticalType)
          ? ProductUserTypeEnum.MortgageBroker
          : ProductUserTypeEnum.RealEstate;
        const productUserGroupingType = isIndividual(profile.profileType)
          ? ProductUserTypeGroupingEnum.Individual
          : isTeam(profile.profileType)
            ? ProductUserTypeGroupingEnum.Team
            : ProductUserTypeGroupingEnum.Office;

        return {
          code: profile.profileCode,
          productUserType,
          productUserGroupingType,
        };
      }),
      rmaShareReplay(),
    );
  }

  private getQueryContext(): Observable<BillingUserContext | null> {
    return this.route.queryParams.pipe(
      map((params) => {
        const queryContext = params['asProfile'];

        if (queryContext) {
          const [type, code] = queryContext.split(':');

          const productGroupingTypes = {
            [INDIVIDUAL]: ProductUserTypeGroupingEnum.Individual,
            [TEAM]: ProductUserTypeGroupingEnum.Team,
            [OFFICE]: ProductUserTypeGroupingEnum.Office,
          };

          // SAFETY(legacy): unreviewed, pre-dates this rule.
          const productUserGroupingType = productGroupingTypes[type as VisibleProfileType];

          if (!productUserGroupingType) {
            return null;
          }

          return {
            code: code,
            productUserType: ProductUserTypeEnum.RealEstate,
            productUserGroupingType: productUserGroupingType,
          };
        }

        return null;
      }),
      distinctUntilChanged(),
    );
  }
}
